/**
 * A character's active behaviors, from both sources: the named items they
 * wield (`resolveEquippedItemBehaviors`) and the skill perks they have
 * unlocked (`SKILL_PERKS`, at or above each perk's level). The one fold the
 * host's behavior paths read, with the rules for when a behavior's
 * condition holds. See `types/behaviors.ts`.
 */

import { getWieldedLoadout, type MaybeEquipmentWearer } from './equipped-items';
import { findBehaviorStack, resolveCombinedCombatModifiers } from './essence-statuses';
import { resolveEquippedItemBehaviors } from './item-instances';
import { getNamedItemCatalog, type NamedItemDefinition } from './named-items';
import { isBladeWeapon, isBluntWeapon, isDualWielding, SKILL_PERKS } from './skill-perks';
import type {
    ActiveBehavior,
    BehaviorCondition,
    BehaviorWeaponClass,
    BlowBehaviorTrigger,
    BlowTriggeredBehavior,
    BlowTriggeredEffect,
    RuleOverrideId,
    TriggeredBehavior,
} from './types/behaviors';
import type { AttackStyle, Character, CharacterEquipmentLoadout, CombatModifierState, Equipment, NPC } from './types/game';
import { assertNever } from './utils';

/** A character whose behaviors can be read: their skills, and what they wield. */
export type BehaviorWearer = MaybeEquipmentWearer;

/**
 * Every behavior a character with `skills` and the wielded `loadout` has
 * active: their named items' (none for an NPC: pass no loadout), then their
 * unlocked perks', each in its source's order.
 */
export function resolveLoadoutBehaviors(
    skills: MaybeEquipmentWearer['skills'],
    loadout: CharacterEquipmentLoadout | undefined,
    namedCatalog: readonly NamedItemDefinition[] = getNamedItemCatalog(),
): ActiveBehavior[] {
    const items = resolveEquippedItemBehaviors(loadout, namedCatalog).map(({ itemId, namedItemId, behavior, behaviorIndex }): ActiveBehavior => ({
        source: { kind: 'item', itemId, namedItemId },
        behavior,
        behaviorIndex,
        key: `item:${itemId}:${behaviorIndex}`,
    }));
    // A perk is active while its skill is at or above the perk's level (`hasSkillPerk`).
    const levelOf = (skillId: string): number => skills?.find((skill) => skill.id === skillId)?.level ?? 0;
    const perks = SKILL_PERKS
        .filter((perk) => perk.behaviors && levelOf(perk.skillId) >= perk.level)
        .flatMap((perk) => (perk.behaviors ?? []).map((behavior, behaviorIndex): ActiveBehavior => ({
            source: { kind: 'perk', perkId: perk.id },
            behavior,
            behaviorIndex,
            key: `perk:${perk.id}:${behaviorIndex}`,
        })));
    return [...items, ...perks];
}

/** Every behavior `wearer` has active (`resolveLoadoutBehaviors`), from what they wield; an NPC's gear carries none. */
export function resolveActiveBehaviors(
    wearer: BehaviorWearer,
    namedCatalog?: readonly NamedItemDefinition[],
): ActiveBehavior[] {
    return resolveLoadoutBehaviors(wearer.skills, wearer.type === 'npc' ? undefined : getWieldedLoadout(wearer), namedCatalog);
}

/** Whether one of `behaviors` breaks or bends this core rule. */
export function isRuleOverridden(behaviors: readonly ActiveBehavior[], rule: RuleOverrideId): boolean {
    return behaviors.some(({ behavior }) => behavior.kind === 'rule-override' && behavior.rule === rule);
}

/** Whether a behavior `wearer` has active breaks or bends this core rule. */
export function hasRuleOverride(
    wearer: BehaviorWearer,
    rule: RuleOverrideId,
    namedCatalog?: readonly NamedItemDefinition[],
): boolean {
    return isRuleOverridden(resolveActiveBehaviors(wearer, namedCatalog), rule);
}

/** A blow a condition is read in: one the wearer strikes, or one that strikes them. */
export interface BehaviorBlow {
    readonly style: AttackStyle;
    /** Whether the wearer strikes it (else they are struck). */
    readonly isStriker: boolean;
    /** What the striker strikes with; none bare-handed. */
    readonly weapon?: Equipment;
    /** The striker's hits in a row on the struck, this one included, if it connects. */
    readonly streak?: number;
    /** Once rolled: whether it landed damage. */
    readonly landed?: boolean;
    /** Once rolled: how far up the weapon's damage range it landed, 0-1. */
    readonly damageQuality?: number;
}

/** What a condition is read against. */
export interface BehaviorContext {
    readonly wearer: Character;
    readonly nowMs: number;
    /** The other character in the blow or the moment, when there is one. */
    readonly opponent?: Character;
    readonly blow?: BehaviorBlow;
    /** How many enemies stand within `radiusTiles` of the wearer; without it, none do. */
    readonly countEnemiesNear?: (wearer: Character, radiusTiles: number) => number;
}

function essenceShare(character: Character): number {
    return character.maxEssence > 0 ? character.essence / character.maxEssence : 0;
}

function isWeaponClass(blow: BehaviorBlow | undefined, weaponClass: BehaviorWeaponClass): boolean {
    if (!blow?.isStriker || blow.style !== 'melee') {
        return false;
    }
    switch (weaponClass) {
        case 'blade':
            return isBladeWeapon(blow.weapon);
        case 'blunt':
            return isBluntWeapon(blow.weapon);
        case 'unarmed':
            return blow.weapon === undefined;
        default:
            return assertNever(weaponClass);
    }
}

/** Whether `condition` holds in `context`. */
export function isBehaviorConditionMet(condition: BehaviorCondition, context: BehaviorContext): boolean {
    const { wearer, opponent, blow, nowMs } = context;
    switch (condition.kind) {
        case 'target-role':
            return opponent?.type === 'npc' && (opponent as NPC).role === condition.role;
        case 'essence-above':
            return essenceShare(wearer) > condition.poolShare;
        case 'essence-below':
            return essenceShare(wearer) < condition.poolShare;
        case 'standing-still': {
            const lastMovedAtMs = Math.max(wearer.lastMovedAtMs ?? 0, condition.countsShots ? wearer.lastRangedShotAtMs ?? 0 : 0);
            return nowMs - lastMovedAtMs >= condition.ms;
        }
        case 'enemies-near':
            return (context.countEnemiesNear?.(wearer, condition.radiusTiles) ?? 0) >= condition.count;
        case 'attack-style':
            return blow !== undefined && condition.styles.includes(blow.style);
        case 'weapon-class':
            return isWeaponClass(blow, condition.weaponClass);
        case 'dual-wielding':
            return isDualWielding(getWieldedLoadout(wearer));
        case 'target-unhurt':
            return opponent !== undefined && opponent.essence >= opponent.maxEssence;
        case 'target-essence-below':
            return opponent !== undefined && opponent.maxEssence > 0 && opponent.essence < opponent.maxEssence * condition.poolShare;
        case 'has-stack': {
            const on = condition.on === 'self' ? wearer : opponent;
            const stack = on ? findBehaviorStack(on, condition.stackId, nowMs) : undefined;
            return stack !== undefined && (!condition.fromWearer || stack.sourceId === wearer.id);
        }
        case 'hit-streak':
            return blow?.streak !== undefined && blow.streak % condition.every === 0;
        case 'strong-hit':
            return blow?.landed === true && (blow.damageQuality ?? 0) >= condition.minQuality;
        case 'all-of':
            return condition.conditions.every((part) => isBehaviorConditionMet(part, context));
        default:
            return assertNever(condition);
    }
}

/** What `behaviors`' conditional modifiers add in `context`, folded (`resolveCombinedCombatModifiers`). */
export function resolveBehaviorModifiers(behaviors: readonly ActiveBehavior[], context: BehaviorContext): CombatModifierState {
    return resolveCombinedCombatModifiers(behaviors.flatMap(({ behavior }) => (
        behavior.kind === 'conditional' && (!behavior.condition || isBehaviorConditionMet(behavior.condition, context))
            ? [behavior.modifiers]
            : []
    )));
}

const BLOW_TRIGGERS: Readonly<Record<BlowBehaviorTrigger, true>> = {
    connect: true,
    miss: true,
    dodged: true,
    blocked: true,
    parried: true,
};

/** Whether a triggered behavior is run by combat as a blow resolves (rather than on the next tick). */
export function isBlowTriggeredBehavior(behavior: TriggeredBehavior): behavior is BlowTriggeredBehavior {
    return behavior.trigger in BLOW_TRIGGERS;
}

/** An active blow triggered behavior, narrowed. */
export interface ActiveBlowBehavior {
    readonly active: ActiveBehavior;
    readonly behavior: BlowTriggeredBehavior;
}

/**
 * `behaviors` that `trigger` sets off in `context`: blow triggered behaviors
 * on that trigger (and, for a block, its kind), whose condition holds,
 * optionally only those with an effect of `effectKind`. Chance is the
 * caller's to roll.
 */
export function listBlowTriggers(
    behaviors: readonly ActiveBehavior[],
    trigger: BlowBehaviorTrigger,
    context: BehaviorContext,
    options: { readonly blockType?: 'full' | 'partial'; readonly effectKind?: BlowTriggeredEffect['kind'] } = {},
): ActiveBlowBehavior[] {
    return behaviors.flatMap((active) => {
        const { behavior } = active;
        if (behavior.kind !== 'triggered' || !isBlowTriggeredBehavior(behavior) || behavior.trigger !== trigger) {
            return [];
        }
        if (behavior.blockType !== undefined && behavior.blockType !== options.blockType) {
            return [];
        }
        if (options.effectKind !== undefined && behavior.effect.kind !== options.effectKind) {
            return [];
        }
        return !behavior.condition || isBehaviorConditionMet(behavior.condition, context) ? [{ active, behavior }] : [];
    });
}
